/**
 * Add investment / Edit investment — a side panel beside the register.
 *
 * The kind is chosen first and the fields follow it. Each kind says in one
 * line what it is, because the three are easy to confuse and the difference
 * is money: equity is ownership and earns nothing by itself; a profit share is
 * a percentage of a defined profit and never ownership; a vehicle partnership
 * earns through the vehicles linked to it.
 *
 * A draft may be saved half-filled and finished later. An active profit share
 * needs every rule it is calculated by, including what happens to a loss —
 * nothing is assumed. The investor can be chosen or added here.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CALCULATION_PERIODS,
  CURRENCIES,
  DEDUCTIBLE_EXPENSE_LABELS,
  DEDUCTIBLE_EXPENSE_MODES,
  FREQUENCY_LABELS,
  INVESTMENT_STATUSES,
  INVESTMENT_STATUS_LABELS,
  INVESTMENT_TYPES,
  INVESTMENT_TYPE_EXPLANATIONS,
  INVESTMENT_TYPE_LABELS,
  LOSS_TREATMENTS,
  LOSS_TREATMENT_LABELS,
  PAYOUT_FREQUENCIES,
  PROFIT_INCOME_LABELS,
  PROFIT_INCOME_STREAMS,
  VEHICLE_PAYOUT_BASES,
  VEHICLE_PAYOUT_BASIS_LABELS,
  type DeductibleExpenseMode,
  type ExpenseCategory,
  type InvestmentCreateInput,
  type InvestmentDetail,
  type InvestmentScopeInput,
  type InvestmentStatus,
  type InvestmentType,
  type LossTreatment,
  type ProfitIncomeStream,
  type VehiclePayoutBasis,
} from '@cenox/shared';
import { Button, Input, Select, SidePanel, Textarea, cn } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { ScopeSelector } from './ScopeSelector';
import {
  useEntities,
  useInvestorProfiles,
  useSaveEntity,
  useSaveInvestment,
  useSaveInvestorProfile,
  useUploadAgreement,
} from './investmentData';

const CALC_LABELS: Record<(typeof CALCULATION_PERIODS)[number], string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

function scopeInput(i: InvestmentDetail): InvestmentScopeInput {
  const s = i.scope;
  if (s.kind === 'worldwide') return { kind: 'worldwide' };
  if (s.kind === 'country') return { kind: 'country', country: s.country! };
  if (s.kind === 'state') return { kind: 'state', country: s.country!, state: s.state! };
  return { kind: 'region', regionName: s.regionName ?? '', areas: s.areas };
}

export function InvestmentPanel({
  investment,
  investorId: presetInvestor,
  type: presetType,
  onClose,
  onSaved,
}: {
  /** Editing this one; absent to add. */
  investment?: InvestmentDetail;
  investorId?: string;
  type?: InvestmentType;
  onClose: () => void;
  onSaved: (message: string, id: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const defaultCurrency = business?.currencyCode ?? 'NGN';
  const defaultCountry = business?.countryCode ?? 'NG';
  const investors = useInvestorProfiles();
  const entities = useEntities();
  const categories = useQuery({
    queryKey: ['expense-categories', business?.businessId],
    queryFn: () => api.get<{ data: ExpenseCategory[] }>(path('/expense-categories')),
    staleTime: 60_000,
  });
  const save = useSaveInvestment();
  const saveInvestor = useSaveInvestorProfile();
  const saveEntity = useSaveEntity();
  const upload = useUploadAgreement();

  const editing = Boolean(investment);
  const [type, setType] = useState<InvestmentType>(investment?.type ?? presetType ?? 'vehicle_partnership');
  const [investorId, setInvestorId] = useState(investment?.investorId ?? presetInvestor ?? '');
  const [newInvestor, setNewInvestor] = useState({ name: '', phone: '', email: '', address: '' });
  const [status, setStatus] = useState<InvestmentStatus>(investment?.status ?? 'draft');
  const [reference, setReference] = useState(investment?.reference ?? '');
  const [amountInvested, setAmountInvested] = useState(investment?.amountInvested ?? '');
  const [currencyCode, setCurrencyCode] = useState(investment?.currencyCode ?? defaultCurrency);
  const [startsOn, setStartsOn] = useState(investment?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(investment?.endsOn ?? '');
  const [notes, setNotes] = useState(investment?.notes ?? '');
  const [scope, setScope] = useState<InvestmentScopeInput>(investment ? scopeInput(investment) : { kind: 'worldwide' });
  const [vehicleBasis, setVehicleBasis] = useState<VehiclePayoutBasis | ''>(investment?.vehiclePayoutBasis ?? '');
  const [vehicleFixed, setVehicleFixed] = useState(investment?.vehicleFixedAmount ?? '');
  const [vehiclePercent, setVehiclePercent] = useState(investment?.vehiclePercent ?? '');
  const [payoutFrequency, setPayoutFrequency] = useState(investment?.payoutFrequency ?? '');
  const [entityId, setEntityId] = useState(investment?.entity?.id ?? '');
  const [newEntity, setNewEntity] = useState('');
  const [ownershipPercent, setOwnershipPercent] = useState(investment?.ownershipPercent ?? '');
  const [ownershipBasis, setOwnershipBasis] = useState(investment?.ownershipBasis ?? 'Ordinary shares');
  const [profitPercent, setProfitPercent] = useState(investment?.profitPercent ?? '');
  const [calculationPeriod, setCalculationPeriod] = useState(investment?.calculationPeriod ?? 'monthly');
  const [operationalScope, setOperationalScope] = useState(investment?.operationalScope ?? '');
  const [income, setIncome] = useState<ProfitIncomeStream[]>(investment?.eligibleIncome ?? []);
  const [deductible, setDeductible] = useState<DeductibleExpenseMode | ''>(investment?.deductibleExpenses ?? '');
  const [categoryIds, setCategoryIds] = useState<string[]>(investment?.deductibleCategories.map((c) => c.id) ?? []);
  const [loss, setLoss] = useState<LossTreatment | ''>(investment?.lossTreatment ?? '');
  const [overlapOk, setOverlapOk] = useState(investment?.overlapAcknowledged ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const error = save.error ?? saveInvestor.error ?? saveEntity.error ?? upload.error;
  const fields = (error instanceof ApiError ? error.fields : undefined) as Record<string, string[] | undefined> | undefined;
  const pending = save.isPending || saveInvestor.isPending || saveEntity.isPending || upload.isPending;
  const investorOptions = useMemo(
    () => (investors.data ?? []).map((i) => ({ value: i.id, label: i.reference ? `${i.name} (${i.reference})` : i.name })),
    [investors.data],
  );

  const body = (): Omit<InvestmentCreateInput, 'investorId' | 'type'> => {
    const blank = (v: string) => (v.trim() ? v.trim() : null);
    return {
      status,
      reference: blank(reference),
      amountInvested: blank(amountInvested),
      currencyCode: blank(currencyCode),
      startsOn: blank(startsOn),
      endsOn: blank(endsOn),
      notes: blank(notes),
      scope,
      payoutFrequency: (payoutFrequency || null) as InvestmentCreateInput['payoutFrequency'],
      ...(type === 'vehicle_partnership'
        ? {
            vehiclePayoutBasis: vehicleBasis || null,
            vehicleFixedAmount: vehicleBasis === 'fixed_per_period' ? blank(vehicleFixed) : null,
            vehiclePercent: vehicleBasis === 'percent_of_remittance' ? blank(vehiclePercent) : null,
          }
        : {}),
      ...(type === 'equity'
        ? { entityId: entityId || null, ownershipPercent: blank(ownershipPercent), ownershipBasis: blank(ownershipBasis) }
        : {}),
      ...(type === 'profit_sharing'
        ? {
            profitPercent: blank(profitPercent),
            calculationPeriod: calculationPeriod as InvestmentCreateInput['calculationPeriod'],
            operationalScope: blank(operationalScope),
            eligibleIncome: income,
            deductibleExpenses: deductible || null,
            deductibleCategoryIds: deductible === 'selected' ? categoryIds : null,
            lossTreatment: loss || null,
            overlapAcknowledged: overlapOk,
          }
        : {}),
    } as Omit<InvestmentCreateInput, 'investorId' | 'type'>;
  };

  const submit = async () => {
    setProblem(null);
    try {
      let who = investorId;
      if (!editing && !who) {
        if (!newInvestor.name.trim()) {
          setProblem('Choose the investor, or enter the new investor’s name.');
          return;
        }
        const made = await saveInvestor.mutateAsync({
          id: null,
          input: {
            name: newInvestor.name.trim(),
            ...(newInvestor.phone.trim() ? { phone: newInvestor.phone.trim() } : {}),
            ...(newInvestor.email.trim() ? { email: newInvestor.email.trim() } : {}),
            ...(newInvestor.address.trim() ? { address: newInvestor.address.trim() } : {}),
          },
        });
        who = made.data.id;
        setInvestorId(who);
      }
      let entity = entityId;
      if (type === 'equity' && !entity && newEntity.trim()) {
        const made = await saveEntity.mutateAsync({ id: null, input: { name: newEntity.trim() } });
        entity = made.data.id;
        setEntityId(entity);
        setNewEntity('');
      }
      const payload = { ...body(), ...(type === 'equity' ? { entityId: entity || null } : {}) };
      const saved = await save.mutateAsync(
        investment ? { id: investment.id, input: payload } : { id: null, input: { ...payload, investorId: who, type } as InvestmentCreateInput },
      );
      if (file) await upload.mutateAsync({ id: saved.data.id, file });
      onSaved(
        investment ? `${INVESTMENT_TYPE_LABELS[type]} saved.` : `${INVESTMENT_TYPE_LABELS[type]} added for ${saved.data.investorName}.`,
        saved.data.id,
      );
    } catch {
      // Shown from the mutation's error below; what was typed stays.
    }
  };

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="investment-form"
        loading={pending}
        disabled={pending}
        className="bg-violet-600 hover:bg-violet-700 active:bg-violet-800"
      >
        {editing ? 'Save changes' : 'Add investment'}
      </Button>
    </div>
  );

  return (
    <SidePanel
      open
      onClose={onClose}
      title={editing ? `Edit ${INVESTMENT_TYPE_LABELS[type].toLowerCase()}` : 'Add investment'}
      subtitle={editing ? investment!.investorName : 'An investor can hold several investments of different kinds.'}
      footer={footer}
    >
      <form
        id="investment-form"
        aria-label={editing ? 'Edit investment' : 'Add investment'}
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <fieldset>
          <legend className="label-base">Investment type</legend>
          <div className="grid gap-2" role="radiogroup" aria-label="Investment type">
            {INVESTMENT_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={type === t}
                disabled={editing && type !== t}
                onClick={() => setType(t)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  type === t ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300' : 'border-slate-200 hover:border-violet-200',
                  editing && type !== t && 'hidden',
                )}
              >
                <span className="font-medium text-slate-900">{INVESTMENT_TYPE_LABELS[t]}</span>
                <span className="block text-xs text-slate-500">{INVESTMENT_TYPE_EXPLANATIONS[t]}</span>
              </button>
            ))}
          </div>
        </fieldset>

        {editing ? null : (
          <section className="space-y-3">
            <Select
              label="Investor"
              value={investorId}
              placeholder="New investor…"
              options={investorOptions}
              onChange={(e) => setInvestorId(e.target.value)}
              error={fields?.investorId?.[0]}
            />
            {investorId ? null : (
              <div className="grid gap-3 rounded-lg border border-dashed border-violet-200 p-3 sm:grid-cols-2">
                <Input label="Name" required value={newInvestor.name} onChange={(e) => setNewInvestor({ ...newInvestor, name: e.target.value })} error={fields?.name?.[0]} />
                <Input label="Phone" value={newInvestor.phone} onChange={(e) => setNewInvestor({ ...newInvestor, phone: e.target.value })} error={fields?.phone?.[0]} />
                <Input label="Email" type="email" value={newInvestor.email} onChange={(e) => setNewInvestor({ ...newInvestor, email: e.target.value })} error={fields?.email?.[0]} />
                <Input label="Address" value={newInvestor.address} onChange={(e) => setNewInvestor({ ...newInvestor, address: e.target.value })} />
              </div>
            )}
          </section>
        )}

        <section className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Status"
            value={status}
            options={INVESTMENT_STATUSES.map((s) => ({ value: s, label: INVESTMENT_STATUS_LABELS[s] }))}
            onChange={(e) => setStatus(e.target.value as InvestmentStatus)}
            hint="A draft may be incomplete."
            error={fields?.status?.[0]}
          />
          <Input label="Agreement reference" value={reference} onChange={(e) => setReference(e.target.value)} error={fields?.reference?.[0]} />
          <Input label="Amount invested" inputMode="decimal" value={amountInvested} onChange={(e) => setAmountInvested(e.target.value)} error={fields?.amountInvested?.[0]} />
          <Select
            label="Currency"
            value={currencyCode}
            options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
            onChange={(e) => setCurrencyCode(e.target.value)}
            error={fields?.currencyCode?.[0]}
          />
          <Input label="Effective start date" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} error={fields?.startsOn?.[0]} />
          <Input label="End date" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} hint="Optional" error={fields?.endsOn?.[0]} />
        </section>

        {type === 'vehicle_partnership' ? (
          <section className="space-y-3 rounded-lg bg-slate-50 p-3">
            <h3 className="text-sm font-semibold text-slate-900">Payout terms</h3>
            <Select
              label="Calculation basis"
              value={vehicleBasis}
              placeholder="Not agreed yet"
              options={VEHICLE_PAYOUT_BASES.map((b) => ({ value: b, label: VEHICLE_PAYOUT_BASIS_LABELS[b] }))}
              onChange={(e) => setVehicleBasis(e.target.value as VehiclePayoutBasis | '')}
              error={fields?.vehiclePayoutBasis?.[0]}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              {vehicleBasis === 'fixed_per_period' ? (
                <Input label={`Amount per vehicle a week (${currencyCode})`} inputMode="decimal" value={vehicleFixed} onChange={(e) => setVehicleFixed(e.target.value)} error={fields?.vehicleFixedAmount?.[0]} />
              ) : null}
              {vehicleBasis === 'percent_of_remittance' ? (
                <Input label="Percentage of remittance" inputMode="decimal" value={vehiclePercent} onChange={(e) => setVehiclePercent(e.target.value)} error={fields?.vehiclePercent?.[0]} />
              ) : null}
              <Select
                label="Payout frequency"
                value={payoutFrequency}
                placeholder="Not agreed yet"
                options={PAYOUT_FREQUENCIES.map((f) => ({ value: f, label: FREQUENCY_LABELS[f] }))}
                onChange={(e) => setPayoutFrequency(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">
              Earnings follow each linked vehicle and its dated rider assignments, one share per vehicle-week. Link vehicles
              from the investor’s profile.{' '}
              {editing && investment!.vehicles.length
                ? 'Saving here sets the terms for vehicles linked from now on; to apply new terms to the linked vehicles from a date, use Change vehicle terms on the profile.'
                : ''}
            </p>
          </section>
        ) : null}

        {type === 'equity' ? (
          <section className="space-y-3 rounded-lg bg-slate-50 p-3">
            <h3 className="text-sm font-semibold text-slate-900">Ownership</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Business or legal entity"
                value={entityId}
                placeholder={entities.data?.length ? 'Choose…' : 'Add one below'}
                options={(entities.data ?? []).map((e) => ({ value: e.id, label: e.name }))}
                onChange={(e) => setEntityId(e.target.value)}
                error={fields?.entityId?.[0]}
              />
              {entityId ? null : (
                <Input label="New entity" value={newEntity} onChange={(e) => setNewEntity(e.target.value)} placeholder="e.g. Ceno Logistics Ltd" />
              )}
              <Input label="Ownership percentage" inputMode="decimal" value={ownershipPercent} onChange={(e) => setOwnershipPercent(e.target.value)} error={fields?.ownershipPercent?.[0]} />
              <Input label="Ownership basis" value={ownershipBasis} onChange={(e) => setOwnershipBasis(e.target.value)} hint="What the percentage is of" error={fields?.ownershipBasis?.[0]} />
            </div>
            <p className="text-xs text-slate-500">
              Ownership is recorded apart from payouts. Owning equity earns nothing automatically: money reaches a holder
              only through a dividend declared and posted. A state or region below records coverage only — it never implies
              ownership of the whole entity beyond the percentage held.
            </p>
          </section>
        ) : null}

        {type === 'profit_sharing' ? (
          <section className="space-y-3 rounded-lg bg-slate-50 p-3">
            <h3 className="text-sm font-semibold text-slate-900">Profit-sharing terms</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <Input label="Profit percentage" inputMode="decimal" value={profitPercent} onChange={(e) => setProfitPercent(e.target.value)} error={fields?.profitPercent?.[0]} />
              <Select
                label="Calculation period"
                value={calculationPeriod}
                options={CALCULATION_PERIODS.map((p) => ({ value: p, label: CALC_LABELS[p] }))}
                onChange={(e) => setCalculationPeriod(e.target.value as typeof calculationPeriod)}
                error={fields?.calculationPeriod?.[0]}
              />
              <Select
                label="Payout frequency"
                value={payoutFrequency}
                placeholder="Not agreed yet"
                options={PAYOUT_FREQUENCIES.map((f) => ({ value: f, label: FREQUENCY_LABELS[f] }))}
                onChange={(e) => setPayoutFrequency(e.target.value)}
              />
            </div>
            <Textarea label="Operational scope" rows={2} value={operationalScope} onChange={(e) => setOperationalScope(e.target.value)} hint="What part of the business the agreement covers, in words" />
            <fieldset>
              <legend className="label-base">Eligible income</legend>
              <div className="grid gap-1 sm:grid-cols-2">
                {PROFIT_INCOME_STREAMS.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="size-4 accent-violet-600"
                      checked={income.includes(s)}
                      onChange={(e) => setIncome(e.target.checked ? [...income, s] : income.filter((x) => x !== s))}
                    />
                    {PROFIT_INCOME_LABELS[s]}
                  </label>
                ))}
              </div>
              {fields?.eligibleIncome?.[0] ? <p role="alert" className="mt-1 text-xs font-medium text-red-600">{fields.eligibleIncome[0]}</p> : null}
            </fieldset>
            <fieldset>
              <legend className="label-base">Deductible expenses</legend>
              <div className="space-y-1">
                {DEDUCTIBLE_EXPENSE_MODES.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="deductible" className="size-4 accent-violet-600" checked={deductible === m} onChange={() => setDeductible(m)} />
                    {DEDUCTIBLE_EXPENSE_LABELS[m]}
                  </label>
                ))}
              </div>
              {deductible === 'selected' ? (
                <div className="mt-2 grid max-h-40 gap-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-2" aria-label="Expense categories">
                  {(categories.data?.data ?? []).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="size-4 accent-violet-600"
                        checked={categoryIds.includes(c.id)}
                        onChange={(e) => setCategoryIds(e.target.checked ? [...categoryIds, c.id] : categoryIds.filter((x) => x !== c.id))}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              ) : null}
              {fields?.deductibleExpenses?.[0] ?? fields?.deductibleCategoryIds?.[0] ? (
                <p role="alert" className="mt-1 text-xs font-medium text-red-600">{fields?.deductibleExpenses?.[0] ?? fields?.deductibleCategoryIds?.[0]}</p>
              ) : null}
            </fieldset>
            <fieldset>
              <legend className="label-base">Losses</legend>
              <div className="space-y-1">
                {LOSS_TREATMENTS.map((l) => (
                  <label key={l} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="loss" className="size-4 accent-violet-600" checked={loss === l} onChange={() => setLoss(l)} />
                    {LOSS_TREATMENT_LABELS[l]}
                  </label>
                ))}
              </div>
              {fields?.lossTreatment?.[0] ? <p role="alert" className="mt-1 text-xs font-medium text-red-600">{fields.lossTreatment[0]}</p> : null}
            </fieldset>
            {fields?.overlapAcknowledged || overlapOk ? (
              <label className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                <input type="checkbox" className="mt-0.5 size-4" checked={overlapOk} onChange={(e) => setOverlapOk(e.target.checked)} />
                This investor already shares profit in some of these places. Share profit there with them again anyway.
              </label>
            ) : null}
            <p className="text-xs text-slate-500">
              A profit share is an agreed share of a defined profit. It never creates ownership or voting rights, and the
              calculation is shown in full before anything is posted.
            </p>
          </section>
        ) : null}

        <ScopeSelector value={scope} onChange={setScope} defaultCountry={defaultCountry} errors={fields} />

        <section className="space-y-3">
          <div>
            <label htmlFor="agreement-file" className="label-base">
              Agreement
            </label>
            <input
              id="agreement-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-violet-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-violet-700"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <p className="mt-1 text-xs text-slate-500">
              {investment?.agreement ? `On file: ${investment.agreement.filename}. Choosing a file replaces it.` : 'PDF or photo of the signed agreement.'}
            </p>
          </div>
          <Textarea label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </section>

        {problem ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{problem}</p>
        ) : error ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error instanceof ApiError ? error.message : 'Could not save. What you typed is still here — try again.'}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
